import React, { useState } from 'react';
import { Input, Button, Pill, PillTones, Text, InputSizes } from '@clockout-dev/design-elements';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import ImportListModal from './ImportListModal';
import styles from '../styles/userEligibilityForm.module.scss';

/**
 * Member whitelist editor. Manages ALLOW overrides for granting specific member access.
 * Calls handlers that manage API calls and state updates.
 *
 * `readOnly` hides every write control (Import, the add row, per-row
 * remove) rather than merely disabling them — the session either lacks
 * admin:access_list:manage entirely or is view-only on Configurations, and
 * the underlying handlers are themselves guarded, so a hidden control here
 * is defense in depth, not the only thing standing between this session and
 * a write.
 */
function WhitelistEditor({ whitelist = [], onAdd, onRemove, onBulkSuccess, onBulkUpload, onPollBulkStatus, readOnly = false }) {
    const [draft, setDraft] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const submit = () => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        if (whitelist.some(m => m.member_id === trimmed)) return;

        onAdd?.(trimmed);
        setDraft('');
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submit();
        }
    };

    const isDuplicate = draft.trim() !== '' && whitelist.some(m => m.member_id === draft.trim());

    return (
        <div className={styles.whitelistEditor}>
            <div className={styles.editorHeader}>
                <Text as="span" size="xs" className={styles.whitelistDescription}>
                    Add member IDs to grant specific users access to the service.
                </Text>
                {!readOnly ? (
                    <Button
                        type="button"
                        icon={<DownloadOutlined />}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Import List
                    </Button>
                ) : null}
            </div>
            {!readOnly ? (
                <div className={styles.whitelistInputRow}>
                    <Input
                        id="whitelistMemberId"
                        size={InputSizes.SMALL}
                        label="Member ID"
                        placeholder="Enter member ID..."
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={handleKeyDown}
                        classNames={{ root: styles.whitelistInput }}
                    />
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={!draft.trim() || isDuplicate}
                        className={styles.whitelistAddButton}
                    >
                        Add
                    </Button>
                </div>
            ) : null}

            {!readOnly ? (
                <ImportListModal
                    mode="whitelist"
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={(summary) => {
                        setIsModalOpen(false);
                        onBulkSuccess?.(summary);
                    }}
                    onUpload={onBulkUpload}
                    onPollStatus={onPollBulkStatus}
                />
            ) : null}
            {whitelist.length > 0 ? (
                <div className={styles.whitelistMembers}>
                    <Text as="span" size="xs" className={styles.whitelistCount}>
                        {`Whitelisted Members (${whitelist.length})`}
                    </Text>
                    <ul className={styles.whitelistList}>
                        {whitelist.map((member) => (
                            <li key={member.id || member.member_id} className={styles.whitelistItem}>
                                <Pill
                                    label={member.member_id}
                                    tone={PillTones.NEUTRAL}
                                />
                                {!readOnly ? (
                                    <button
                                        type="button"
                                        aria-label={`Remove ${member.member_id}`}
                                        className={styles.whitelistRemoveButton}
                                        onClick={() => onRemove?.(member.member_id, member.id)}
                                    >
                                        <CloseOutlined style={{ fontSize: 11 }} />
                                    </button>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <Text as="span" size="xs" className={styles.whitelistEmpty}>
                    No members added yet.
                </Text>
            )}
        </div>
    );
}

export default WhitelistEditor;
